import { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';

export interface HelpResource {
  id: string;
  type: 'tips' | 'contact' | 'donation';
  title: string;
  content: string;
  link: string | null;
  created_at: string;
  updated_at: string;
  is_active: boolean;
}

// Nouvelle interface pour la configuration globale d'animation
export interface HelpButtonConfig {
  animation_enabled: boolean;
}

interface useHelpResourcesReturnType {
  resources: HelpResource[];
  loading: boolean;
  tipsLink: string | null;
  contactLink: string | null;
  donationLink: string | null;
  // Nouveau: indique si l'animation est activée
  animationEnabled: boolean;
  refreshResources: () => Promise<void>;
}

export function useHelpResources(): useHelpResourcesReturnType {
  const [resources, setResources] = useState<HelpResource[]>([]);
  const [loading, setLoading] = useState(true);
  const [tipsLink, setTipsLink] = useState<string | null>(null);
  const [contactLink, setContactLink] = useState<string | null>(null);
  const [donationLink, setDonationLink] = useState<string | null>(null);
  const [animationEnabled, setAnimationEnabled] = useState<boolean>(false);
  const [realtimeConnected, setRealtimeConnected] = useState(false);
  const isLoadingRef = useRef(false);
  const errorCountRef = useRef(0);
  const lastLoadTimeRef = useRef(0);

  const getLatestLinkForType = async (type: 'tips' | 'contact' | 'donation') => {
    try {
      console.log(`[HelpResources] Récupération du lien ${type}...`);
      const { data, error } = await supabase
        .from('help_resources')
        .select('link')
        .eq('type', type)
        .eq('is_active', true)
        .order('updated_at', { ascending: false })
        .limit(1)
        .single();

      if (error) {
        console.error(`[HelpResources] Erreur lors de la récupération du lien ${type}:`, error);
        return null;
      }

      console.log(`[HelpResources] Lien ${type} trouvé:`, data?.link);
      return data?.link || null;
    } catch (error) {
      console.error(`[HelpResources] Erreur dans getLatestLinkForType pour ${type}:`, error);
      return null;
    }
  };

  // Nouvelle fonction pour récupérer la configuration d'animation
  const getHelpButtonConfig = async () => {
    try {
      const { data, error } = await supabase
        .from('help_button_config')
        .select('animation_enabled')
        .single();

      if (error) {
        console.error('[HelpResources] Erreur lors de la récupération de la configuration du bouton d\'aide:', error);
        return false;
      }

      return data?.animation_enabled || false;
    } catch (error) {
      console.error('[HelpResources] Erreur dans getHelpButtonConfig:', error);
      return false;
    }
  };

  const loadResources = async () => {
    if (isLoadingRef.current) {
      console.log('[HelpResources] Chargement déjà en cours, ignoré');
      return;
    }

    const now = Date.now();
    if (now - lastLoadTimeRef.current < 5000) {
      console.log('[HelpResources] Trop tôt pour recharger, ignoré');
      return;
    }
    
    lastLoadTimeRef.current = now;
    isLoadingRef.current = true;
    setLoading(true);

    try {
      console.log('[HelpResources] Chargement des ressources...');
      
      const { data: allResources, error: resourcesError } = await supabase
        .from('help_resources')
        .select('*')
        .eq('is_active', true)
        .order('updated_at', { ascending: false });

      if (resourcesError) {
        console.error('[HelpResources] Erreur lors du chargement des ressources:', resourcesError);
        errorCountRef.current += 1;
        return;
      }

      console.log('[HelpResources] Ressources chargées:', allResources);
      setResources(allResources || []);
      errorCountRef.current = 0;
      
      const latestTipsLink = await getLatestLinkForType('tips');
      const latestContactLink = await getLatestLinkForType('contact');
      const latestDonationLink = await getLatestLinkForType('donation');
      
      console.log('[HelpResources] Configuration des liens:', { 
        tips: latestTipsLink, 
        contact: latestContactLink,
        donation: latestDonationLink 
      });
      
      setTipsLink(latestTipsLink);
      setContactLink(latestContactLink);
      setDonationLink(latestDonationLink);
      
      // Récupération de l'état d'animation
      const isAnimationEnabled = await getHelpButtonConfig();
      setAnimationEnabled(isAnimationEnabled);
      console.log('[HelpResources] Animation du bouton d\'aide:', isAnimationEnabled ? 'ACTIVÉE' : 'DÉSACTIVÉE');
      
    } catch (error) {
      console.error('[HelpResources] Erreur dans loadResources:', error);
      errorCountRef.current += 1;
    } finally {
      isLoadingRef.current = false;
      setLoading(false);
    }
  };

  // Vérifier périodiquement les ressources
  useEffect(() => {
    // Vérifier immédiatement
    loadResources();
    
    // Puis vérifier périodiquement toutes les 2 minutes
    const intervalId = setInterval(() => {
      console.log('[HelpResources] Vérification périodique des ressources...');
      if (!realtimeConnected || errorCountRef.current > 0) {
        loadResources();
      }
    }, 120000); // 2 minutes
    
    return () => {
      clearInterval(intervalId);
    };
  }, [realtimeConnected]);

  useEffect(() => {
    console.log('[HelpResources] Configuration de l\'effet');
    
    loadResources();

    // Configurer la connexion Realtime pour détecter les changements
    const channel = supabase
      .channel('help_resources_changes')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'help_resources'
      }, (payload) => {
        console.log('[HelpResources] Notification de changement reçue:', payload);
        // Forcer le rechargement immédiat lors d'un changement
        loadResources();
        console.log('%c[HelpResources] MISE À JOUR DÉTECTÉE!', 'background: #ff0000; color: white; padding: 2px 5px; border-radius: 3px;');
      })
      .subscribe((status) => {
        console.log('[HelpResources] Statut de l\'abonnement:', status);
        if (status !== 'SUBSCRIBED') {
          console.error('[HelpResources] Erreur de souscription Realtime:', status);
          setRealtimeConnected(false);
        } else {
          console.log('%c[HelpResources] Souscription Realtime activée avec succès!', 'background: #00ff00; color: black; padding: 2px 5px; border-radius: 3px;');
          setRealtimeConnected(true);
        }
      });

    // Configurer un autre canal pour surveiller les changements dans la table help_button_config
    const configChannel = supabase
      .channel('help_button_config_changes')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'help_button_config'
      }, (payload) => {
        console.log('[HelpResources] Changement de configuration du bouton d\'aide détecté:', payload);
        // Mettre à jour l'état d'animation
        if (payload.new && 'animation_enabled' in payload.new) {
          setAnimationEnabled(payload.new.animation_enabled);
          console.log('[HelpResources] Animation du bouton d\'aide:', 
            payload.new.animation_enabled ? 'ACTIVÉE' : 'DÉSACTIVÉE');
        }
      })
      .subscribe();

    return () => {
      console.log('[HelpResources] Nettoyage des abonnements');
      channel.unsubscribe();
      configChannel.unsubscribe();
    };
  }, []);

  return {
    resources,
    loading,
    tipsLink,
    contactLink,
    donationLink,
    animationEnabled,
    refreshResources: loadResources
  };
} 